import { useEffect, useRef } from 'react';
import anime from 'animejs';
import type { CVConfig } from '../../config/cvConfig';

interface AboutProps {
  personal: CVConfig['personal'];
}

export function About({ personal }: AboutProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate about content
            anime({
              targets: '.about-content',
              translateY: [50, 0],
              opacity: [0, 1],
              duration: 1000,
              delay: (_el, i) => i * 200,
              easing: 'easeOutQuart'
            });

            // Animate stats
            anime({
              targets: '.stat-number',
              innerHTML: (el) => [0, el.getAttribute('data-value')],
              duration: 2000,
              delay: 500,
              easing: 'easeOutQuart',
              round: 1
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="about" 
      className="py-20 px-4 sm:px-6 lg:px-8 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-orbitron font-bold neon-text mb-4">
            About Me
          </h2>
          <p className="text-xl text-white/70 font-rajdhani max-w-3xl mx-auto">
            Pioneering the intersection of artificial intelligence and creative content
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="about-content">
              <h3 className="text-2xl font-orbitron font-bold text-[var(--synthwave-neon)] mb-4">
                The Future of Content Creation
              </h3>
              <p className="text-lg font-rajdhani text-white/80 leading-relaxed mb-6">
                As a freelance AI content generator, I specialize in harnessing the power of artificial intelligence 
                to create compelling, engaging, and highly effective content across multiple platforms and industries.
              </p>
              <p className="text-lg font-rajdhani text-white/80 leading-relaxed mb-6">
                My expertise spans from developing custom GPT models to implementing automated content workflows 
                that scale creativity without compromising quality. I work with forward-thinking companies to 
                revolutionize their content strategies through AI-powered solutions.
              </p>
            </div>

            <div className="about-content">
              <h4 className="text-xl font-orbitron font-bold text-[var(--synthwave-primary)] mb-4">
                What I Bring to the Table
              </h4>
              <ul className="space-y-3 font-rajdhani text-white/80">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-[var(--synthwave-neon)] rounded-full mr-3"></div>
                  Advanced AI model integration and customization
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-[var(--synthwave-neon)] rounded-full mr-3"></div>
                  Scalable content generation systems
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-[var(--synthwave-neon)] rounded-full mr-3"></div>
                  Brand voice training and consistency
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-[var(--synthwave-neon)] rounded-full mr-3"></div>
                  Multi-platform content optimization
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-[var(--synthwave-neon)] rounded-full mr-3"></div>
                  Performance analytics and optimization
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="about-content bg-black/40 backdrop-blur-sm rounded-2xl p-6 neon-border">
              <h4 className="text-xl font-orbitron font-bold text-[var(--synthwave-secondary)] mb-4">
                Let's Connect
              </h4>
              <div className="space-y-3 font-rajdhani">
                <div className="flex items-center text-white/80">
                  <svg className="w-5 h-5 mr-3 text-[var(--synthwave-neon)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {personal.email}
                </div>
                {personal.phone && (
                  <div className="flex items-center text-white/80">
                    <svg className="w-5 h-5 mr-3 text-[var(--synthwave-neon)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {personal.phone}
                  </div>
                )}
                <div className="flex items-center text-white/80">
                  <svg className="w-5 h-5 mr-3 text-[var(--synthwave-neon)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {personal.location}
                </div>
              </div>
            </div>
          </div>

          {/* Stats & Visual */}
          <div className="space-y-8">
            {/* Achievement Stats */}
            <div className="about-content grid grid-cols-2 gap-6">
              <div className="text-center p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
                <div className="stat-number text-4xl font-orbitron font-bold text-[var(--synthwave-primary)]" data-value="500">
                  0
                </div>
                <div className="text-sm font-rajdhani text-white/70 mt-2">Projects Completed</div>
              </div>
              
              <div className="text-center p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
                <div className="stat-number text-4xl font-orbitron font-bold text-[var(--synthwave-secondary)]" data-value="50">
                  0
                </div>
                <div className="text-sm font-rajdhani text-white/70 mt-2">Happy Clients</div>
              </div>
              
              <div className="text-center p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
                <div className="stat-number text-4xl font-orbitron font-bold text-[var(--synthwave-accent)]" data-value="1000000">
                  0
                </div>
                <div className="text-sm font-rajdhani text-white/70 mt-2">Words Generated</div>
              </div>
              
              <div className="text-center p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
                <div className="stat-number text-4xl font-orbitron font-bold text-[var(--synthwave-neon)]" data-value="99">
                  0
                </div>
                <div className="text-sm font-rajdhani text-white/70 mt-2">Satisfaction Rate</div>
              </div>
            </div>

            {/* Philosophy */}
            <div className="about-content bg-gradient-to-br from-[var(--synthwave-primary)]/10 to-[var(--synthwave-secondary)]/10 backdrop-blur-sm rounded-2xl p-6 neon-border">
              <h4 className="text-xl font-orbitron font-bold text-[var(--synthwave-neon)] mb-4">
                My Philosophy
              </h4>
              <blockquote className="text-lg font-rajdhani text-white/90 italic leading-relaxed">
                "The future belongs to those who can seamlessly blend human creativity with artificial intelligence. 
                I don't just create content—I architect experiences that resonate, engage, and convert."
              </blockquote>
            </div>

            {/* Technologies */}
            <div className="about-content">
              <h4 className="text-xl font-orbitron font-bold text-[var(--synthwave-accent)] mb-4">
                Core Technologies
              </h4>
              <div className="flex flex-wrap gap-3">
                {['GPT-4', 'Claude', 'Midjourney', 'Python', 'React', 'Node.js', 'TensorFlow', 'AWS'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-sm font-rajdhani text-[var(--synthwave-neon)] neon-border"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}